package ai.jessie.web.theme

import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.until

// Typography helpers

/** Dank Mono with tabular figures; every number uses it. */
val NumStyle = CssStyle.base {
    css(
        "font-family" to JessieTokens.FONT_NUM,
        "font-weight" to NumWeightVar.value(),
        "font-variant-numeric" to "tabular-nums",
        "font-feature-settings" to "\"liga\" 0",
    )
}

val BrandStyle = CssStyle.base {
    css(
        "font-family" to JessieTokens.FONT_BRAND,
        "font-style" to "italic",
        "letter-spacing" to "-0.01em",
    )
}

val LabelStyle = CssStyle.base {
    css(
        "font-size" to "12px",
        "font-weight" to "600",
        "letter-spacing" to "0.05em",
        "text-transform" to "uppercase",
        "color" to InkMutedVar.value(),
    )
}

val MutedStyle = CssStyle.base { css("color" to InkMutedVar.value()) }

val SmallStyle = CssStyle.base { css("font-size" to "13px") }

// Layout helpers

val RowStyle = CssStyle.base {
    css("display" to "flex", "align-items" to "center", "gap" to "8px", "flex-wrap" to "wrap")
}

val StackStyle = CssStyle.base {
    css("display" to "flex", "flex-direction" to "column", "gap" to "8px")
}

val Grid2Style = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "repeat(2, minmax(0, 1fr))",
            "gap" to "16px",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

val Grid3Style = CssStyle {
    base {
        css(
            "display" to "grid",
            "grid-template-columns" to "repeat(3, minmax(0, 1fr))",
            "gap" to "16px",
        )
    }
    until(Breakpoint.LG) { css("grid-template-columns" to "minmax(0, 1fr)") }
}

/** Keeps the line breaks of a stored multi-line value, e.g. a postal address. */
val PreLineStyle = CssStyle.base { css("white-space" to "pre-line") }

/** A bulleted list inside a definition value or card: no outer margin, compact indent. */
val SubListStyle = CssStyle.base { css("margin" to "0", "padding-left" to "18px") }

val VisuallyHiddenStyle = CssStyle.base {
    css(
        "position" to "absolute",
        "width" to "1px",
        "height" to "1px",
        "overflow" to "hidden",
        "clip" to "rect(0 0 0 0)",
        "white-space" to "nowrap",
    )
}

// Status and messages

val StatusStyle = CssStyle.base { css("font-size" to "13px", "color" to InkMutedVar.value()) }

val AlertStyle = CssStyle {
    base {
        css(
            "padding" to "12px 14px",
            "border-radius" to JessieTokens.RADIUS_CONTROL,
            "background" to DangerTintVar.value(),
            "color" to DangerVar.value(),
            "font-size" to "14px",
        )
    }
    // Links name the correction in the alert's own colour (components.md, Komponenten II).
    cssRule(" a") { css("color" to DangerVar.value(), "text-decoration" to "underline") }
}

/** A list of corrections in the alert colours; bullets stay inside the box. */
val AlertListStyle = AlertStyle.extendedByBase {
    css("margin" to "0", "padding" to "12px 14px 12px 32px")
}

val HintStyle = CssStyle.base {
    css(
        "padding" to "12px 14px",
        "border-radius" to JessieTokens.RADIUS_CONTROL,
        "border" to "1px solid ${LineVar.value()}",
        "background" to SurfaceMutedVar.value(),
        "font-size" to "13px",
        "color" to Ink3Var.value(),
    )
}

/** Confirms a completed action; the caller keeps its own `role="status"`. */
val NoticeStyle = CssStyle.base {
    css(
        "padding" to "12px 14px",
        "border-radius" to JessieTokens.RADIUS_CONTROL,
        "background" to HoursTintVar.value(),
        "color" to HoursVar.value(),
        "font-size" to "14px",
        "font-weight" to "500",
    )
}
